import React from 'react';
import { Table, Spin } from 'antd';
import { primaryShades, variantTokens } from '../../../theme/theme';
import EmptyCard from '../../EmptyCard';

/**
 * StyledTable - Componente tabella riutilizzabile con stile unificato
 * 
 * @param {Object} props - Proprietà del componente
 * @param {Array} props.columns - Array di definizioni delle colonne per la tabella
 * @param {Array} props.dataSource - Array di dati da mostrare nella tabella
 * @param {string} props.rowKey - Chiave unica per identificare ogni riga (default: "id")
 * @param {boolean} props.loading - Stato di caricamento della tabella
 * @param {Object} props.pagination - Configurazione della paginazione (null per disabilitarla)
 * @param {string} props.size - Dimensione della tabella: "small", "middle" o "large" (default: "small")
 * @param {Object} props.scroll - Proprietà di scorrimento della tabella
 * @param {Function} props.onChange - Handler per cambiamenti nella tabella (paginazione, ordinamento)
 * @param {string} props.className - Classi CSS aggiuntive
 * @param {string} props.containerClassName - Classi CSS aggiuntive per il container
 * @param {Object} props.tableStyle - Stili addizionali per la tabella
 * @param {Object} props.containerStyle - Stili addizionali per il container
 * @param {Function} props.rowClassName - Funzione per determinare le classi CSS per ogni riga
 * @param {React.ReactNode} props.resetButton - Pulsante di reset filtri (opzionale)
 * @param {Object} props.expandable - Configurazione per righe espandibili
 * @param {boolean} props.showSorterTooltip - Stato di visualizzazione del tooltip per i sorter (default: false)
 * @returns {JSX.Element}
 */
const StyledTable = ({
    columns,
    dataSource,
    rowKey = 'id',
    loading = false,
    pagination = {
        defaultPageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
    },
    size = 'small',
    scroll = { x: 'max-content' },
    onChange,
    className = '',
    containerClassName = '',
    tableStyle = {},
    containerStyle = {},
    rowClassName,
    resetButton,
    expandable,
    showSorterTooltip = false,
    ...otherProps
}) => {
    const defaultPagination = {
        defaultPageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        ...pagination
    };

    // Se non ci sono dati e non è in caricamento, mostra EmptyCard
    if (!loading && (!dataSource || dataSource.length === 0)) {
        return <EmptyCard />;
    }

    return (
        <div className={`table-container ${containerClassName}`} style={containerStyle}>
            <Spin spinning={loading}>
                <Table
                    columns={columns}
                    dataSource={dataSource}
                    rowKey={rowKey}
                    pagination={pagination === null ? false : defaultPagination}
                    size={size}
                    scroll={scroll}
                    onChange={onChange}
                    className={`analytics-table ${className}`}
                    rowClassName={rowClassName}
                    expandable={expandable}
                    style={{ width: '100%', ...tableStyle }}
                    showSorterTooltip={showSorterTooltip}
                    {...otherProps}
                />
                {resetButton && <div className="reset-filter-container">{resetButton}</div>}
            </Spin>

            <style jsx="true">{`
                .table-container {
                    border: 1px solid #d9d9d9;
                    border-radius: 8px;
                    overflow: hidden;
                    position: relative;
                    background-color: white;
                    box-shadow: none;
                    border: 1px solid #e0e0e0;
                }
                
                /* Rimuove i margini anomali nelle tabelle nidificate */
                .analytics-table .ant-table.ant-table-middle .ant-table-tbody .ant-table-wrapper:only-child .ant-table {
                    margin-block: 0 !important;
                    margin-inline: 0 !important;
                }
                
                .analytics-table .ant-pagination-item {
                    background: transparent;
                    border: none;
                }
                
                .analytics-table .ant-pagination-item-active {
                    background: ${primaryShades.primary300};
                }
                
                .analytics-table .ant-pagination-item-active a {
                    color: ${variantTokens.white};
                }
                
                .analytics-table .ant-pagination {
                    padding-right: 16px;
                }
                
                .analytics-table th.ant-table-cell {
                    text-align: left;
                }
                
                .analytics-table td.ant-table-cell {
                    text-align: left;
                }
                
                .analytics-table th.ant-table-cell[aria-sort]:not([aria-sort="none"]) {
                    background: ${primaryShades.primary50};
                }
                
                .analytics-table .ant-table-column-sorter-up.active,
                .analytics-table .ant-table-column-sorter-down.active {
                    color: ${primaryShades.primary500};
                }
                
                .reset-filter-container {
                    position: absolute;
                    bottom: 16px;
                    left: 16px;
                    z-index: 1;
                }
            `}</style>
        </div>
    );
};

export default StyledTable; 